import React, { useCallback } from 'react'

import Link from 'next/link'
import { HiMiniXMark } from 'react-icons/hi2'
import {
  TbLayoutSidebarLeftCollapse,
  TbLayoutSidebarLeftExpand,
} from 'react-icons/tb'
import { twMerge } from 'tailwind-merge'

import { CommercialOnly } from '@/components/common/CommercialOnly'
import IconButton from '@/components/common/IconButtonV2'
import LogoIcon from '@/components/common/LogoIcon'
import {
  useNavigationOptions,
  useSidebar,
} from '@/components/common/Sidebar/hooks'
import { isCommercialNavigationOption } from '@/components/common/Sidebar/lib'
import SidebarOption from '@/components/common/Sidebar/Option'
import UserMenu from '@/components/common/Sidebar/UserMenu'

import { useBillingOrganization } from '@/hooks/billing/useBillingOrganization'

import { useSidebarStore } from '@/stores/SidebarStore'

export const LEGACY_SIDEBAR_WIDTH_COLLAPSED = 56
export const LEGACY_SIDEBAR_WIDTH_EXPANDED = 226

interface LegacySidebarProps {
  expanded: boolean
}

export const LegacySidebar: React.FC<LegacySidebarProps> = ({ expanded }) => {
  const { navigationOptions } = useNavigationOptions()
  const {
    expanded: sidebarExpanded,
    toggleSidebar,
    setExpanded,
  } = useSidebarStore()

  const { data: billingOrganization } = useBillingOrganization()

  const toggleExpanded = useCallback(() => {
    setExpanded(!sidebarExpanded)
  }, [sidebarExpanded, setExpanded])

  const { userCanAccess } = useSidebar()

  return (
    <div className={twMerge('flex flex-col items-center h-full w-full px-3')}>
      <div className="flex items-center justify-between w-full pl-[2px] min-h-[52px]">
        <Link
          className="flex flex-1 items-center gap-x-2 w-full pt-[2px]"
          href="/"
        >
          <div className="w-[30px] h-[30px]">
            <LogoIcon className="h-[30px] w-[30px]" />
          </div>
          {expanded && (
            <div className="w-36 transition-opacity duration-200">
              <span className="text-sm font-semibold text-primary-900 dark:text-primary-100">
                Sweetspot
              </span>
            </div>
          )}
        </Link>
        {expanded && (
          <IconButton
            type="button"
            className="block md:hidden"
            onClick={toggleSidebar}
          >
            <HiMiniXMark className="w-5 h-5" />
          </IconButton>
        )}
      </div>
      <div className="flex flex-col flex-1 justify-between w-full mt-1">
        <div className="flex flex-col w-full gap-y-2">
          <nav className="flex flex-1 flex-col h-full justify-between divide-y divide-primary-200 dark:divide-primary-700">
            <ul role="list" className="flex flex-col gap-y-[3px]">
              {navigationOptions.map((item) => {
                const hasAccess = userCanAccess(item)
                const navItem = (
                  <li className="flex justify-start w-full min-h-8">
                    <SidebarOption
                      option={item}
                      canAccess={hasAccess}
                      expanded={expanded}
                    />
                  </li>
                )

                return isCommercialNavigationOption(item) ? (
                  <CommercialOnly key={item.id}>{navItem}</CommercialOnly>
                ) : (
                  <React.Fragment key={item.id}>{navItem}</React.Fragment>
                )
              })}
            </ul>
          </nav>
        </div>
      </div>
      {billingOrganization !== null && billingOrganization !== undefined && (
        <div
          className={twMerge(
            'flex relative w-full gap-y-3 pb-4',
            expanded
              ? 'flex-row-reverse justify-start'
              : 'flex-col items-center',
          )}
        >
          <div className={twMerge('flex', expanded ? 'pt-[2px] w-[24px]' : '')}>
            <IconButton
              onClick={toggleExpanded}
              className="hidden md:flex text-primary-500 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-700 rounded-md"
              tooltip={expanded ? 'Collapse Sidebar' : 'Expand Sidebar'}
              tooltipProps={{ delayDuration: 0 }}
              tooltipContentProps={{
                side: 'right',
              }}
            >
              {expanded ? (
                <div className="flex items-center gap-x-2">
                  <TbLayoutSidebarLeftCollapse className="w-5 h-5 stroke-[1.7px]" />
                </div>
              ) : (
                <div className="flex items-center gap-x-2">
                  <TbLayoutSidebarLeftExpand className="w-5 h-5 stroke-[1.7px]" />
                </div>
              )}
            </IconButton>
          </div>
          <div
            className={twMerge(
              'flex items-center ',
              expanded ? 'w-full md:w-[calc(100%-25px)]' : '',
            )}
          >
            <UserMenu expanded={expanded} />
          </div>
        </div>
      )}
    </div>
  )
}

export default LegacySidebar
